import { refreshTokens } from "./srpLogin.js";

import {
  clearAuthTokens,
  getAuthTokens,
  storeAuthTokens,
} from "./cookieUtils.js";

const REFRESH_LOCK_KEY = "auth_refresh_lock";
const REFRESH_LOCK_TTL_MS = 15_000; // lock expires after 15s in case of crash
const REFRESH_TIMEOUT_MS = 10_000;
const ACCESS_TOKEN_SAFETY_WINDOW_MS = 2 * 60 * 1000;

class TokenRefreshManager {
  constructor() {
    // If a refresh is in progress in THIS tab, everyone awaits this
    this.currentRefreshPromise = null;

    // When we last successfully refreshed (in-memory, per tab)
    this.lastRefreshAt = 0;

    // Small cooldown so we don't hammer Cognito right after a refresh (per tab)
    this.MIN_REFRESH_INTERVAL_MS = 5_000; // 5 seconds

    // Unique ID for this tab
    this.tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // True when a refresh was sent but we never stored a response (timeout / freeze)
    this.refreshAttemptUnacked = false;
  }

  isPageSafeToRefresh() {
    if (typeof document === "undefined") return false;
    if (document.hidden || document.visibilityState !== "visible") return false;
    if (!navigator.onLine) return false;
    return true;
  }

  /**
   * JWT-only expiry check used by WebSocket reconnect.
   * Does not use tokenRefreshedAt age, so we don't burn a refresh token
   * just because the socket dropped while the access token is still valid.
   */
  isAccessTokenNearExpiry(user) {
    const token = user?.accessToken || user?.idToken;
    const exp = this.getTokenExpiration(token);
    if (!exp) return false;
    return exp * 1000 - Date.now() < ACCESS_TOKEN_SAFETY_WINDOW_MS;
  }

  // --- Helpers ----------------------------

  getStoredUser() {
    return getAuthTokens();
  }

  saveUser(user) {
    storeAuthTokens(user);
  }

  getTokenExpiration(token) {
    if (!token) return null;
    try {
      const [, payloadB64] = token.split(".");
      if (!payloadB64) return null;

      const payloadJson = atob(payloadB64);
      const payload = JSON.parse(payloadJson);

      return typeof payload.exp === "number" ? payload.exp : null;
    } catch {
      return null;
    }
  }

  /**
   * Decide if we SHOULD refresh.
   * Uses:
   * - cooldown (5 seconds)
   * - tokenRefreshedAt if present
   * - falls back to JWT `exp` (2 minute safety window)
   */
  needsRefresh(user, maxAgeMinutes = 50) {
    const now = Date.now();

    // Cooldown: if we *just* refreshed in this tab, don't do it again
    if (now - this.lastRefreshAt < this.MIN_REFRESH_INTERVAL_MS) {
      return false;
    }

    if (!user) return true;

    // Preferred: use `tokenRefreshedAt` if we've set it previously
    if (user.tokenRefreshedAt) {
      const ageMs = now - user.tokenRefreshedAt;
      const maxAgeMs = maxAgeMinutes * 60 * 1000;
      return ageMs > maxAgeMs;
    }

    // Fallback: use access token / id token expiry
    const token = user.accessToken || user.idToken;
    const exp = this.getTokenExpiration(token); // seconds
    if (!exp) return true;

    return exp * 1000 - now < ACCESS_TOKEN_SAFETY_WINDOW_MS;
  }

  // ---- Cross-tab lock helpers ------------

  getLock() {
    try {
      const raw = localStorage.getItem(REFRESH_LOCK_KEY);
      if (!raw) return null;

      const lock = JSON.parse(raw);
      if (!lock || typeof lock.ts !== "number" || !lock.tabId) return null;

      // If lock is too old, consider it stale
      if (Date.now() - lock.ts > REFRESH_LOCK_TTL_MS) {
        localStorage.removeItem(REFRESH_LOCK_KEY);
        return null;
      }

      return lock;
    } catch {
      return null;
    }
  }

  tryAcquireLock() {
    const existing = this.getLock();
    if (existing) {
      return false;
    }

    const lock = { tabId: this.tabId, ts: Date.now() };

    try {
      localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
    } catch {
      return false;
    }

    // Double-check that WE own it
    const stored = this.getLock();
    return stored && stored.tabId === this.tabId;
  }

  releaseLock() {
    const lock = this.getLock();
    if (lock && lock.tabId === this.tabId) {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  }

  waitForOtherTabRefresh(maxWaitMs = 10_000) {
    return new Promise((resolve) => {
      const start = Date.now();

      const done = () => {
        window.removeEventListener("storage", onStorage);
        resolve();
      };

      const onStorage = (e) => {
        // Another tab released the lock
        if (e.key === REFRESH_LOCK_KEY) {
          done();
        }
      };

      window.addEventListener("storage", onStorage);

      const timer = setTimeout(() => {
        // Timeout: stop waiting, we'll decide what to do next
        done();
      }, maxWaitMs);
    });
  }

  // --- Public API -------------------------

  /**
   * Ensure the user has a fresh token.
   *
   * - If token is still fresh → returns the given user (or stored user).
   * - If a refresh is already in progress in THIS tab → waits for that promise.
   * - If another tab is refreshing → waits for storage events, then returns updated user.
   * - Otherwise → becomes "leader", refreshes once, others will wait.
   */
  async ensureFreshToken(user, maxAgeMinutes = 55) {
    const currentUser = user || this.getStoredUser();

    // First check: maybe we don't need to refresh at all
    if (!this.needsRefresh(currentUser, maxAgeMinutes)) {
      return currentUser;
    }

    if (!this.isPageSafeToRefresh()) {
      return currentUser;
    }

    // If a refresh is already in-flight in THIS tab, reuse it
    if (this.currentRefreshPromise) {
      console.log("[TokenRefreshManager] Reusing in-flight refresh promise (same tab)");
      return this.currentRefreshPromise;
    }

    // Cross-tab: try to take the lock
    if (!this.tryAcquireLock()) {
      // Another tab is refreshing; wait for it to finish
      console.log("[TokenRefreshManager] Another tab is refreshing; waiting for result");
      await this.waitForOtherTabRefresh();

      // After waiting, re-read latest user from storage
      const updatedUser = this.getStoredUser();
      return updatedUser || currentUser;
    }

    // This tab is the leader, do the refresh
    console.log("[TokenRefreshManager] Starting token refresh (this tab is leader)");
    this.currentRefreshPromise = this._refreshOnce(currentUser);

    try {
      const refreshedUser = await this.currentRefreshPromise;
      return refreshedUser;
    } finally {
      this.currentRefreshPromise = null;
      this.releaseLock();
    }
  }

  /**
   * Force an immediate refresh regardless of age.
   * Useful for "Refresh Now" buttons, or login flows.
   * Also uses cross-tab lock to avoid reuse errors.
   */
  async refreshUserToken(user) {
    const currentUser = user || this.getStoredUser();

    if (!this.isPageSafeToRefresh()) {
      return currentUser;
    }

    if (this.currentRefreshPromise) {
      return this.currentRefreshPromise;
    }

    // Same cross-tab logic as ensureFreshToken
    if (!this.tryAcquireLock()) {
      console.log("[TokenRefreshManager] Another tab is refreshing; waiting for result");
      await this.waitForOtherTabRefresh();
      const updatedUser = this.getStoredUser();
      return updatedUser || currentUser;
    }

    this.currentRefreshPromise = this._refreshOnce(currentUser);

    try {
      const refreshedUser = await this.currentRefreshPromise;
      return refreshedUser;
    } finally {
      this.currentRefreshPromise = null;
      this.releaseLock();
    }
  }

  // --- Internal refresh logic -------------

  _persistRefreshedTokens(effectiveUser, refreshed) {
    const updatedUser = {
      ...(this.getStoredUser() || effectiveUser),
      idToken: refreshed.idToken,
      accessToken: refreshed.accessToken,
      refreshToken: refreshed.refreshToken || effectiveUser.refreshToken,
      tokenRefreshedAt: Date.now(),
    };

    this.saveUser(updatedUser);
    this.lastRefreshAt = Date.now();
    this.refreshAttemptUnacked = false;
    window.dispatchEvent(
      new CustomEvent("userStateUpdate", { detail: { user: updatedUser } })
    );

    return updatedUser;
  }

  _raceWithTimeout(promise) {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        const timeoutError = new Error("Token refresh timed out");
        timeoutError.code = "REFRESH_TIMEOUT";
        reject(timeoutError);
      }, REFRESH_TIMEOUT_MS);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
      clearTimeout(timeoutId);
    });
  }

  async _refreshOnce(user) {
    const storedUser = this.getStoredUser();
    const effectiveUser = storedUser || user;

    if (!effectiveUser?.refreshToken) {
      this._handleRefreshFailure(
        new Error("Missing refresh token; user must sign in again.")
      );
    }

    if (!navigator.onLine || !this.isPageSafeToRefresh()) {
      console.log(
        "[TokenRefreshManager] Skipping token refresh - page is hidden or offline"
      );
      return effectiveUser;
    }

    const refreshPromise = refreshTokens({
      region: process.env.REACT_APP_AWS_REGION,
      clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
      refreshToken: effectiveUser.refreshToken,
    });

    // If the race times out, Cognito may still complete later. Persist if it does
    // so a rotated refresh token is not discarded after a hung tab.
    refreshPromise
      .then((refreshed) => {
        if (refreshed?.accessToken) {
          this._persistRefreshedTokens(effectiveUser, refreshed);
        }
      })
      .catch(() => {});

    try {
      const refreshed = await this._raceWithTimeout(refreshPromise);
      return this._persistRefreshedTokens(effectiveUser, refreshed);
    } catch (err) {
      if (err?.code === "REFRESH_TIMEOUT" || !err?.$metadata) {
        this.refreshAttemptUnacked = true;
      }

      this._handleRefreshFailure(err);
    }
  }

  _handleRefreshFailure(error) {
    console.error("Token refresh failed:", error);

    const hasHttpResponse = !!error?.response;
    const status = error?.response?.status;
    const awsStatus = error?.$metadata?.httpStatusCode;
    const awsName = error?.name || "";
    const data = error?.response?.data;
    const bodyError =
      (typeof data === "string" ? data : data?.error || data?.error_description) ||
      "";
    const message = error?.message || "";
    const code = error?.code || "";
    const combinedMessage = `${bodyError} ${message} ${awsName}`;

    const isInvalidRefreshToken =
      awsName === "NotAuthorizedException" ||
      ((status === 400 ||
        status === 401 ||
        awsStatus === 400 ||
        awsStatus === 401) &&
        /invalid[_ ]?(grant|token)|expired|refresh token|revoked/i.test(
          combinedMessage
        ));

    const hasAwsResponse = awsStatus != null;
    const noHttpResponse = !hasHttpResponse && !hasAwsResponse;
    const isTimeout = code === "REFRESH_TIMEOUT";

    const looksLikeNetworkErrorByMessageOrCode =
      isTimeout ||
      message.includes("Network Error") ||
      message.includes("Failed to fetch") ||
      message.includes("ERR_NETWORK_IO_SUSPENDED") ||
      message.includes("Network I/O suspended") ||
      message.includes("network I/O suspended") ||
      code === "ERR_NETWORK_IO_SUSPENDED" ||
      message.includes("ERR_NAME_NOT_RESOLVED") ||
      message.includes("Name not resolved") ||
      code === "ERR_NAME_NOT_RESOLVED" ||
      message.includes("ERR_NETWORK_CHANGED") ||
      message.includes("Network changed") ||
      code === "ERR_NETWORK_CHANGED" ||
      message.includes("ERR_INTERNET_DISCONNECTED") ||
      message.includes("Internet disconnected") ||
      code === "ERR_INTERNET_DISCONNECTED";

    const looksLikeServerIssueStatus =
      status === 500 ||
      status === 502 ||
      status === 503 ||
      status === 504 ||
      awsStatus === 500 ||
      awsStatus === 502 ||
      awsStatus === 503 ||
      awsStatus === 504;

    const isNetworkError =
      noHttpResponse || looksLikeNetworkErrorByMessageOrCode;
    const isTransientServerError =
      (hasHttpResponse || hasAwsResponse) && looksLikeServerIssueStatus;

    if (isNetworkError || isTransientServerError) {
      console.log(
        "[TokenRefreshManager] Transient network/server problem - preserving session, will retry later:",
        message || status || awsStatus
      );

      const networkError = new Error(
        "Network or server temporarily unavailable. Your session is still active."
      );
      networkError.code = "NETWORK_ERROR";
      networkError.originalError = error;
      throw networkError;
    }

    if (isInvalidRefreshToken) {
      if (this.refreshAttemptUnacked) {
        console.log(
          "[TokenRefreshManager] invalid_grant after unacked refresh - preserving session"
        );
        const authError = new Error(
          "Could not confirm session refresh. Your session is still active."
        );
        authError.code = "SESSION_EXPIRED";
        authError.originalError = error;
        throw authError;
      }

      console.log(
        "[TokenRefreshManager] Refresh token invalid/expired, clearing session"
      );

      clearAuthTokens();
      localStorage.removeItem(REFRESH_LOCK_KEY);

      const authError = new Error(
        "Your session has expired. Please sign in again."
      );
      authError.code = "SESSION_EXPIRED";
      authError.originalError = error;
      throw authError;
    }

    console.log(
      "[TokenRefreshManager] Unexpected token refresh error (not invalid token, not pure network). Preserving session.",
      { status, awsStatus, bodyError, code }
    );

    const genericError = new Error(
      "Could not refresh session due to an unexpected error. Your session is still active."
    );
    genericError.code = "TOKEN_REFRESH_ERROR";
    genericError.originalError = error;
    throw genericError;
  }
  
}

export const tokenRefreshManager = new TokenRefreshManager();
